import React, { lazy } from 'react';
import { LinkDataType } from './internal-links';
import HomePageZoocasa from './themed-home-page-index/homepage-zoocasa';
import { RouteName } from 'components/dynamic-page/route-matchers';
import { HeadDataType } from 'components/head-data';
import {
  EXP_COMMERCIAL_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US_TENANT_ID,
  getTenantById,
} from '@tenants';

import type { INamedContents } from 'pages';
import type { ListingCardData } from 'components/listing-card';
import dynamic from 'next/dynamic';
import { PropsWithTenantId } from '@tenants/types';
import { useTranslation } from 'react-i18next';

export type HomePageProps = PropsWithTenantId<{
  isCrawler: boolean;
  featuredHomesMlsNums: INamedContents['featuredHomesMlsNums'];
  isNotificationBannerHidden: boolean;
  availableHomeListings: ListingCardData[];
  availableHomeListingsMeta: number;
  availableHomeListingsLocation: string;
  internalLinksData: LinkDataType[];
  isExpandedArea?: boolean;
  maintenance?: Maintenance;
  routeName?: RouteName;
  headerData?: HeadDataType;
  soldHomeListings?: ListingCardData[];
  featuredHomeListings?: ListingCardData[];
}>;

export interface Maintenance {
  isMaintenance: boolean;
  maintenanceStartTime: string;
  maintenanceEndTime: string;
  maintenanceDate: string;
}

const HomePageExpCA = lazy(() => import('./themed-home-page-index/homepage-exp-ca'));
const HomePageExpUS = lazy(() => import('./themed-home-page-index/homepage-exp-us'));
const HomePageExpInService = lazy(() => import('./themed-home-page-index/homepage-exp-in-service'));
const HomePageExpCommercial = dynamic(() => import('./themed-home-page-index/homepage-exp-commercial'), {
  loading: () => <div>Loading...</div>,
});


export default function HomePage(props: HomePageProps) {
  const { tenantId } = props;
  const tenant = getTenantById(tenantId);
  const { t, i18n, ready } = useTranslation(tenant.localization.namespaces);

  // manually set the maintenance props for the homepage and change when needed -- values are currently dummy values
  const maintenance = { isMaintenance: false, maintenanceStartTime: 'Start time', maintenanceEndTime: 'End time', maintenanceDate: 'Date' };

  if (!ready) {
    return null;
  }

  switch (props.tenantId) {
    case EXP_REALTY_CA_TENANT_ID:
      return <HomePageExpCA availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} />;
    case EXP_REALTY_US_TENANT_ID:
      return <HomePageExpUS availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} />;
    case EXP_IN_SERVICE_TENANT_ID:
      return <HomePageExpInService availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} />;
    case EXP_COMMERCIAL_TENANT_ID:
      return <HomePageExpCommercial availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} t={t} i18n={i18n} tReady={ready} />;
    default:
      return <HomePageZoocasa availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} soldHomeListings={props.soldHomeListings} featuredHomeListings={props.featuredHomeListings} />;
  }
}
